import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import { Flag, Shield, EyeOff, Check, X } from "lucide-react";

const STATUS_OPTIONS = [
    { id: "all", label: "All" },
    { id: "open", label: "Open" },
    { id: "in_review", label: "In review" },
    { id: "resolved", label: "Resolved" },
    { id: "dismissed", label: "Dismissed" },
];

export default function ReportsTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [status, setStatus] = useState("open");
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // { report, action, newStatus? }
    const [selected, setSelected] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState(null); // { status, title }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/reports", {
                params: { status, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [status, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [status]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const onConfirm = async (reason) => {
        if (!dialog) return;
        const { report, action, newStatus } = dialog;
        try {
            if (action === "status") {
                await api.patch(`/admin/reports/${report.id}`, { status: newStatus, reason });
                toast.success(`Report → ${newStatus.replace("_", " ")}`);
            } else if (action === "hide") {
                await api.post(`/admin/reports/${report.id}/hide-target`, { reason });
                toast.success("Reported content hidden");
            }
            setDialog(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((r) => r.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const runBulk = async (newStatus, reason) => {
        try {
            const { data } = await api.post("/admin/reports/bulk", {
                ids: selected,
                status: newStatus,
                reason,
            });
            toast.success(`${data.affected} report(s) updated`);
            setBulkConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((r) => selected.includes(r.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "target_type", label: "Target Type" },
                { key: "target_id", label: "Target ID" },
                { key: "reason", label: "Reason" },
                { key: "status", label: "Status" },
                { key: "reporter_id", label: "Reporter ID" },
                { key: "created_at", label: "Created" },
                { key: "resolution_notes", label: "Resolution" },
            ],
            "reports"
        );
    };

    return (
        <div className="space-y-4">
            {/* Status tabs */}
            <div className="bg-snowcap border border-ridge-line rounded-lg p-2 flex gap-1 overflow-x-auto">
                {STATUS_OPTIONS.map((o) => (
                    <button
                        key={o.id}
                        data-testid={`admin-reports-filter-${o.id}`}
                        onClick={() => setStatus(o.id)}
                        className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all ${
                            status === o.id
                                ? "bg-midnight-pine text-white"
                                : "text-slate-gray hover:bg-alpine-mist"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>

            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="admin-reports-bulk"
                    actions={[
                        { id: "resolve", label: "Resolve", onClick: () => setBulkConfirm({ status: "resolved", title: `Resolve ${selected.length} report(s)?`, requireReason: false }) },
                        { id: "dismiss", label: "Dismiss", onClick: () => setBulkConfirm({ status: "dismissed", title: `Dismiss ${selected.length} report(s)?`, requireReason: false }) },
                        { id: "in-review", label: "Mark in review", onClick: () => setBulkConfirm({ status: "in_review", title: `Mark ${selected.length} report(s) in review?`, requireReason: false }) },
                    ]}
                />
            )}
            {/* List */}
            {loading ? (
                <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-24 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="bg-snowcap border border-ridge-line rounded-lg p-12 text-center">
                    <Flag className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                    <h3 className="font-display font-bold text-lg text-midnight-pine mb-1">No reports</h3>
                    <p className="text-slate-gray text-sm">
                        {status === "open" ? "Nothing waiting on you. " : ""}Try a different status filter.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((r) => (
                        <ReportCard
                            key={r.id}
                            r={r}
                            selected={selected.includes(r.id)}
                            onToggleSelect={() => toggleOne(r.id)}
                            onAction={(action, newStatus) => setDialog({ report: r, action, newStatus })}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {total > pageSize && (
                <div className="flex items-center justify-between p-4">
                    <span className="text-xs text-slate-gray font-semibold">
                        Page {page} of {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="admin-reports-prev"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                        >
                            Prev
                        </button>
                        <button
                            data-testid="admin-reports-next"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            <AdminConfirmDialog
                open={!!dialog}
                onClose={() => setDialog(null)}
                onConfirm={onConfirm}
                title={
                    dialog?.action === "hide"
                        ? "Hide this content?"
                        : dialog?.action === "status"
                        ? `Mark report as "${dialog?.newStatus?.replace("_", " ")}"?`
                        : ""
                }
                description={
                    dialog?.action === "hide"
                        ? "Soft-hide the reported item from public view. Author and admins can still see it."
                        : "This will be saved to the audit log."
                }
                confirmLabel={dialog?.action === "hide" ? "Hide content" : "Update"}
                danger={dialog?.action === "hide"}
            />

            <AdminConfirmDialog
                open={!!bulkConfirm}
                onClose={() => setBulkConfirm(null)}
                onConfirm={(reason) => runBulk(bulkConfirm.status, reason)}
                title={bulkConfirm?.title || ""}
                description="Applies to every selected report. Logged to the audit trail."
                confirmLabel="Apply"
                requireReason={bulkConfirm?.requireReason}
            />
        </div>
    );
}

function ReportCard({ r, selected, onToggleSelect, onAction }) {
    const isResolved = r.status === "resolved" || r.status === "dismissed";
    return (
        <div
            data-testid={`admin-report-${r.id}`}
            className={`bg-snowcap border rounded-lg p-5 ${
                isResolved ? "border-ridge-line opacity-80" : "border-ember-orange/40"
            }`}
        >
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <input
                        type="checkbox"
                        data-testid={`admin-report-cb-${r.id}`}
                        checked={!!selected}
                        onChange={onToggleSelect}
                        className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                            {r.target_type}
                        </span>
                        <ReportStatusPill status={r.status} />
                    </div>
                    <h3 className="font-display font-bold text-base text-midnight-pine break-words">
                        <TargetLink target_type={r.target_type} target_id={r.target_id} />
                    </h3>
                    <p className="text-sm text-granite mt-1 leading-snug">{r.reason || "(no reason given)"}</p>
                </div>
                </div>
                <span className="text-xs text-slate-gray whitespace-nowrap shrink-0">
                    {new Date(r.created_at).toLocaleString()}
                </span>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-ridge-line">
                <div className="text-xs text-slate-gray">
                    Reported by{" "}
                    <span className="font-semibold text-granite">
                        {r.reporter?.name || r.reporter_id}
                    </span>
                </div>
                {!isResolved && (
                    <div className="flex flex-wrap gap-2">
                        {r.status === "open" && (
                            <button
                                data-testid={`admin-report-${r.id}-in-review`}
                                onClick={() => onAction("status", "in_review")}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line hover:border-midnight-pine"
                            >
                                <Shield className="w-3 h-3" /> Mark in review
                            </button>
                        )}
                        <button
                            data-testid={`admin-report-${r.id}-hide`}
                            onClick={() => onAction("hide")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-ember-orange/10 text-ember-dark hover:bg-ember-orange hover:text-white transition-colors"
                        >
                            <EyeOff className="w-3 h-3" /> Hide content
                        </button>
                        <button
                            data-testid={`admin-report-${r.id}-resolve`}
                            onClick={() => onAction("status", "resolved")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-deep-evergreen/15 text-deep-evergreen hover:bg-deep-evergreen hover:text-white transition-colors"
                        >
                            <Check className="w-3 h-3" /> Resolve
                        </button>
                        <button
                            data-testid={`admin-report-${r.id}-dismiss`}
                            onClick={() => onAction("status", "dismissed")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line hover:border-slate-gray text-slate-gray"
                        >
                            <X className="w-3 h-3" /> Dismiss
                        </button>
                    </div>
                )}
            </div>

            {r.resolution_notes && (
                <div className="mt-3 pt-3 border-t border-ridge-line text-xs text-slate-gray">
                    <span className="font-bold uppercase tracking-widest">Resolution: </span>
                    {r.resolution_notes}
                </div>
            )}
        </div>
    );
}

function TargetLink({ target_type, target_id }) {
    if (target_type === "adventure" || target_type === "post") {
        return <Link className="hover:text-ember-orange" to={`/adventures/${target_id}`}>{target_id}</Link>;
    }
    if (target_type === "listing") {
        return <Link className="hover:text-ember-orange" to={`/marketplace/${target_id}`}>{target_id}</Link>;
    }
    if (target_type === "user" || target_type === "profile") {
        return <Link className="hover:text-ember-orange" to={`/profile/${target_id}`}>{target_id}</Link>;
    }
    return <span>{target_id}</span>;
}

function ReportStatusPill({ status }) {
    const styles = {
        open: "bg-ember-orange/15 text-ember-dark",
        in_review: "bg-trail-tan/40 text-granite",
        resolved: "bg-deep-evergreen/15 text-deep-evergreen",
        dismissed: "bg-alpine-mist text-slate-gray",
    };
    return (
        <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                styles[status] || styles.open
            }`}
        >
            {(status || "open").replace("_", " ")}
        </span>
    );
}
